import { Global } from "iconsax-react";
import { useEffect, useState } from "react";
import SingleLine from "@/components/ui/singleline";
import ReactSelectPaginate from "@/components/ui/react-select-paginate";
import useDebounce from "@/hooks/use-debounce";
import { Timezone } from "@/hooks/use-timezones";
import Image from "next/image";
import TimezoneIcon from "@/public/static/images/icons/timezone.svg";

// interface TimezoneOption {
//   value: string;
//   label: string;
//   offset: number;
//   abbrev: string;
//   altName: string;
//   [key: string]: any;
// }

const TimezoneSelectWithSearch = ({
  formik,
  options,
  customStyles = {},
  showGlobalIcon = true,
  layperClasses = "",
  isDuplicateEvent = false,
  disabled = false
}: {
  formik: any;
  options: Timezone[];
  customStyles?: any;
  showGlobalIcon?: boolean;
  layperClasses?: string;
  isDuplicateEvent?: boolean;
  disabled?: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filtered, setFiltered] = useState<Timezone[]>(options || []);

  const defaultStyles = {
    container: (base: any) => ({
      ...base,
      minWidth: "270px",
      width: "100%",
      maxWidth: "290px"
    }),
    control: (base: any, state: any) => ({
      ...base,
      borderRadius: "8px",
      border: state.isFocused ? "1px solid #f2f2f2" : "1px solid #f2f2f2", // Indigo focus
      backgroundColor: "#ffffff",
      boxShadow: state.isFocused ? "0 0 0 1px #6366F1" : "none",
      // minHeight: "40px",
      padding: "2px 8px",
      fontWeight: 500,
      fontSize: isDuplicateEvent ? "12px" : "14px",
      color: "#111827",
      "&:hover": {
        borderColor: "#f2f2f2" // lighter indigo
      }
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "#111827", // Gray-900
      fontWeight: 500
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#9CA3AF", // Gray-400
      fontWeight: 400
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: "8px",
      zIndex: 100,
      boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.05)"
    }),
    menuList: (base: any) => ({
      ...base,
      padding: "4px"
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected ? "#EEF2FF" : state.isFocused ? "#F3F4F6" : "#ffffff",
      color: "#111827",
      borderRadius: "6px",
      padding: "8px 12px",
      cursor: "pointer"
    }),
    indicatorSeparator: () => ({
      display: "none"
    })
  };

  const debouncedQuery = useDebounce(searchQuery, 200);

  useEffect(() => {
    if (!formik.values.time_zone && options?.length > 0) {
      const selectedTimezone = options.find((option) =>
        formik.values.editMode ? option.value === formik.values.time_zone : option.select === true
      );
      if (selectedTimezone) {
        formik.setFieldValue("time_zone", selectedTimezone.value);
        formik.setFieldValue("offset", selectedTimezone.offset || "");
      }
      if (formik.values.editMode) {
        formik.setFieldValue("editMode", false);
      }
    }
  }, [options]);

  useEffect(() => {
    if (options?.length > 0) {
      const result = options.filter(
        (option) =>
          option.value.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
          option.abbrev.toLowerCase().includes(debouncedQuery.toLowerCase())
      );
      setFiltered(result);
    }
  }, [debouncedQuery]);

  const getSelectedTimezoneOption = () => {
    const found = options.find((o) => o.value === formik.values.time_zone);
    if (!found) return null;
    return {
      ...found,
      label: `(GMT${found.offset}) ${found.value} (${found.abbrev})`
    };
  };

  const handleChange = (newValue: { value: string; label: string } | null, _actionMeta: any) => {
    if (newValue) {
      const fullOption = options.find((opt) => opt.value === newValue.value);
      if (fullOption) {
        formik.setFieldValue("time_zone", fullOption.value);
        formik.setFieldValue("offset", fullOption.offset);
      }
    } else {
      formik.setFieldValue("time_zone", "");
      formik.setFieldValue("offset", "");
    }
  };

  const loadOptions = async (search: string, loadedOptions: any, { page }: any) => {
    const filtered = options.filter(
      (option) =>
        option.value.toLowerCase().includes(search.toLowerCase()) ||
        option.abbrev.toLowerCase().includes(search.toLowerCase())
    );

    return {
      options: filtered.map((option) => ({
        ...option,
        label: `(GMT${option.offset}) ${option.value} (${option.abbrev})`
      })),
      hasMore: false,
      additional: {
        page: page + 1
      }
    };
  };

  const mergedStyles = {
    container: (base: any, state: any) =>
      customStyles.container ? customStyles.container(base, state) : defaultStyles.container(base),
    control: (base: any, state: any) =>
      customStyles.control ? customStyles.control(base, state) : defaultStyles.control(base, state),
    indicatorSeparator: (base: any) =>
      customStyles.indicatorSeparator
        ? customStyles.indicatorSeparator(base)
        : defaultStyles.indicatorSeparator()
  };

  return (
    <SingleLine
      icon={
        showGlobalIcon ? (
          <Image
            src={TimezoneIcon}
            alt="Timezone Icon"
            width={20}
            height={20}
            className={formik.values.tbdDate ? "text-gray-500 mt-8" : "text-color-primary mt-7"}
          />
        ) : null
      }
      contant={
        <ReactSelectPaginate
          placeholder="Select Timezone"
          loadOptions={loadOptions}
          value={getSelectedTimezoneOption()}
          onChange={handleChange}
          debounce={200}
          isClearable={true}
          maxMenuHeight={180}
          isDisabled={formik.values.tbdDate || disabled}
          components={{
            DropdownIndicator: () => null,
            ClearIndicator: () => null
          }}
          styles={mergedStyles}
        />
      }
      className={layperClasses}
    />
  );
};

export default TimezoneSelectWithSearch;
